import React, { useState } from "react";
import * as PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useFormik, Formik, Form, Field } from 'formik';
import { updateHelpdeskMessage } from '../../store/actions';
import * as Yup from 'yup';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from "../ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Mail } from "lucide-react";

const HelpdeskIntegration = ({
  dispatchUpdateHelpdeskMessage,
  isHelpdeskMessageLoading,
  helpdeskMessage,
}) => {
  const extensionSchema = Yup.object().shape({
    support_email: Yup.string().email().required('Field is required'),
    self_harm_alert_email: Yup.string().email(),
    physical_danger_alert_email: Yup.string().email(),
  });

  return (
    <Card className="border rounded-lg shadow-sm mb-8">
      <CardHeader>
        <CardTitle className="text-xl font-bold">
          Helpdesk Integration
        </CardTitle>
        <CardDescription>
          Configure email addresses for escalation
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <p className="text-gray-700">
            Fill out the form below to set where HubbleIQ escalates help
            requests when it can't resolve a user's issue.
            These addresses will be used to generate tickets or send emails
            directly to your help desk or safety teams.
          </p>
          <Formik
            initialValues={{
              support_email: helpdeskMessage?.support_email || '',
              self_harm_alert_email: helpdeskMessage?.self_harm_alert_email || '',
              physical_danger_alert_email: helpdeskMessage?.physical_danger_alert_email || '',
            }}
            validationSchema={extensionSchema}
            onSubmit={(values) => dispatchUpdateHelpdeskMessage(values)}
            enableReinitialize
          >
            {({ handleChange, values, errors }) => (
              <Form className="form form-helpdesk">
                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <div className="flex items-center">
                      <label
                        htmlFor="helpdeskEmail"
                        className="text-sm font-medium mb-1 flex items-center"
                      >
                        Helpdesk Email address
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="ml-2 text-blue-500 cursor-help">
                                ⓘ
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-sm">
                                Used when an issue needs to be escalated to your
                                general help desk.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </label>
                    </div>
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 mr-2 text-gray-500" />
                      <Input
                        id="helpdesk-email"
                        className={`max-w-md ${errors.support_email ? 'focus-visible:ring-red-500 focus-visible:ring-offset-2 border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                        name="support_email"
                        type="email"
                        value={values.support_email}
                        tag={Field}
                        onChange={(e) => handleChange(e)}
                        disabled={isHelpdeskMessageLoading}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center">
                      <label
                        htmlFor="violenceNotifierEmail"
                        className="text-sm font-medium mb-1 flex items-center"
                      >
                        Physical Danger Notifier
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="ml-2 text-blue-500 cursor-help">
                                ⓘ
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-sm">
                                If physical threats or violence are detected in a
                                chat session, an email will be sent to this address.
                                If not specified, these alerts will default to the
                                helpdesk email.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </label>
                    </div>
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 mr-2 text-gray-500" />
                      <Input
                        id="physical-email"
                        className={`max-w-md ${errors.physical_danger_alert_email ? 'focus-visible:ring-red-500 focus-visible:ring-offset-2 border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                        name="physical_danger_alert_email"
                        type="email"
                        value={values.physical_danger_alert_email}
                        tag={Field}
                        onChange={(e) => handleChange(e)}
                        disabled={isHelpdeskMessageLoading}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center">
                      <label
                        htmlFor="suicidalNotifierEmail"
                        className="text-sm font-medium mb-1 flex items-center"
                      >
                        Self-Harm Notifier
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="ml-2 text-blue-500 cursor-help">
                                ⓘ
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-sm">
                                If self-harm risks are detected in a chat session,
                                an email will be sent to this address. If not
                                specified, these alerts will default to the helpdesk
                                email.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </label>
                    </div>
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 mr-2 text-gray-500" />
                      <Input
                        id="selfharm-email"
                        className={`max-w-md ${errors.self_harm_alert_email ? 'focus-visible:ring-red-500 focus-visible:ring-offset-2 border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                        name="self_harm_alert_email"
                        type="email"
                        value={values.self_harm_alert_email}
                        tag={Field}
                        onChange={(e) => handleChange(e)}
                        disabled={isHelpdeskMessageLoading}
                      />
                    </div>
                  </div>
                </div>
                <div className="pt-4 mt-4 border-t border-solid border-gray-100">
                  <Button
                    type="submit"
                    className="bg-indigo-600 hover:bg-indigo-700"
                    disabled={isHelpdeskMessageLoading}
                  >
                    Save changes
                  </Button>
                </div>
                </Form>
              )}
            </Formik>
        </div>
      </CardContent>
    </Card>
  );
};

HelpdeskIntegration.defaultProps = {};

HelpdeskIntegration.propTypes = {
  dispatchUpdateHelpdeskMessage: PropTypes.func.isRequired,
  isHelpdeskMessageLoading: PropTypes.bool.isRequired,
  helpdeskMessage: PropTypes.shape({
    chrome_head_request: PropTypes.string,
    chrome_sub_head_request: PropTypes.string,
    support_email: PropTypes.string,
    custom_redirect_url: PropTypes.string,
  }),
};

const mapStateToProps = (state) => {
  const {isHelpdeskMessageLoading, helpdeskMessage = {}} = state.EXConfig;
  return {isHelpdeskMessageLoading, helpdeskMessage};
};

const mapDispatchToProps = {
  dispatchUpdateHelpdeskMessage: updateHelpdeskMessage,
};

export default connect(mapStateToProps, mapDispatchToProps)(HelpdeskIntegration);